import { Component, ErrorInfo, ReactNode } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global safety net for print/PDF render failures and any other crashes that
 * would otherwise white-screen the app. Catches React render errors below the
 * main layout and shows a clean localized fallback instead of freezing the UI.
 */
class PrintErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[PrintErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    // Clear any leftover print-mode classes that could leave the UI hidden.
    document.body.classList.remove("pdf-exporting", "print-landscape");
    document.documentElement.classList.remove("pdf-exporting");
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { t } = this.props;
    const title = t("errors.renderTitle", {
      defaultValue: "تعذّر عرض هذه الصفحة",
    });
    const desc = t("errors.renderDesc", {
      defaultValue:
        "حدث خطأ غير متوقع أثناء تجهيز العرض. يمكنك المحاولة مرة أخرى أو العودة للصفحة السابقة.",
    });
    const retry = t("common.retry", { defaultValue: "إعادة المحاولة" });

    return (
      <div
        role="alert"
        className="m-4 rounded-lg border border-destructive/30 bg-destructive/5 p-6 print:hidden"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-destructive">{title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
            {this.state.error?.message && (
              <pre className="mt-3 max-h-32 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground whitespace-pre-wrap">
                {this.state.error.message}
              </pre>
            )}
            <div className="mt-4">
              <Button size="sm" variant="outline" onClick={this.handleReset} className="gap-2">
                <RefreshCw className="h-4 w-4" /> {retry}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const PrintErrorBoundary = withTranslation()(PrintErrorBoundaryInner);
export default PrintErrorBoundary;
